import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LazyImage from '../components/LazyImage';
import { useCart } from '../contexts/CartContext';
import { gtagService } from '../services/gtagService';

const Cart: React.FC = () => {
  const { items, total, updateQuantity, removeFromCart, clearCart } = useCart();
  // const couponCode = 'UC10';
  const discountPercentage = 10;
  const minimumOrderAmount = 3000;
  
  // Calculate price after discount
  const calculateDiscountedPrice = (price: number) => {
    return price - (price * discountPercentage / 100);
  };
  
  // Calculate totals with discount
  const originalTotal = total;
  const discountedTotal = items.reduce((sum, item) => sum + (calculateDiscountedPrice(item.price) * item.quantity), 0);
  const totalSavings = Math.round(originalTotal - discountedTotal);
  const isMinimumOrderMet = discountedTotal >= minimumOrderAmount;

  // Track cart view
  useEffect(() => {
    if (items.length > 0) {
      gtagService.trackViewCart(items, total);
    }
  }, []);

  if (items.length === 0) {
    return (
      <div className="container py-8 sm:py-12 lg:py-16 max-w-4xl">
        <div className="text-center">
          <div className="text-6xl sm:text-8xl mb-6 sm:mb-8">🛒</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-4">Your cart is empty</h1>
          <p className="text-lg sm:text-xl text-neutral-600 mb-6 sm:mb-8">Start shopping to add some amazing firecrackers!</p>
          <Link
            to="/"
            className="bg-accent-600 text-white px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-bold rounded-xl hover:bg-accent-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 sm:py-12 lg:py-16 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900">Shopping Cart</h1>
        <button
          onClick={clearCart}
          className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 self-start sm:self-auto hover:scale-105 hover:shadow-md"
        >
          Clear Cart
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {items.map((item) => {
              const timeUntilExpiration = item.addedAt ? (item.addedAt + (24 * 60 * 60 * 1000)) - Date.now() : 0;
              const hoursLeft = Math.floor(timeUntilExpiration / (60 * 60 * 1000));
              const isExpiringSoon = hoursLeft < 2 && hoursLeft >= 0;

              return (
                <div key={item.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 mx-auto sm:mx-0">
                      <LazyImage
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-md"
                      />
                    </div>
                    
                    <div className="flex-grow text-center sm:text-left">
                      <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-2">{item.name}</h3>
                      <p className="text-neutral-600 text-sm mb-2">Category: {item.category}</p>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <p className="text-lg sm:text-xl font-bold text-green-600">₹{Math.round(calculateDiscountedPrice(item.price))}</p>
                        <p className="text-sm sm:text-base text-neutral-400 line-through">₹{item.price}</p>
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                          {discountPercentage}% OFF
                        </span>
                      </div>
                      {item.addedAt && hoursLeft >= 0 && (
                        <p className={`text-xs mt-1 ${isExpiringSoon ? 'text-warning-600 font-semibold' : 'text-neutral-500'}`}>
                          {isExpiringSoon ? '⏰ ' : ''}
                          Expires in {hoursLeft} hour{hoursLeft !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-10 h-10 rounded-xl bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-700 transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:hover:scale-100"
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="w-14 text-center font-semibold text-neutral-900 text-lg">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-10 h-10 rounded-xl bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center text-lg font-bold text-neutral-700 transition-all duration-200 hover:scale-110"
                      >
                        +
                      </button>
                    </div>

                    <div className="text-center sm:text-right">
                      <p className="text-lg sm:text-xl font-bold text-green-600 mb-1">
                        ₹{Math.round(calculateDiscountedPrice(item.price) * item.quantity).toLocaleString()}
                      </p>
                      <p className="text-sm text-neutral-400 line-through mb-2">
                        ₹{(item.price * item.quantity).toLocaleString()}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:scale-105"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Enquiry Summary</h2>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-neutral-700">
                <span>Original Subtotal ({items.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                <span className="font-semibold">₹{originalTotal.toLocaleString()}</span>
              </div>
              
              {/* Discount Applied */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-green-800">
                      After coupon applied you will save
                    </span>
                  </div>
                  <span className="text-sm font-bold text-green-600">
                    -₹{totalSavings.toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between text-neutral-700">
                <span>Subtotal after discount</span>
                <span className="font-semibold text-green-600">₹{Math.round(discountedTotal).toLocaleString()}</span>
              </div>
              
              <div className="mt-3 mb-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full animate-pulse">
                  <svg className="w-5 h-5 text-blue-600 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-800">
                    Shipping charges will be decided by the lorry service
                  </span>
                </div>
              </div>
              <hr className="my-4" />
              <div className="flex justify-between text-xl font-bold text-neutral-900">
                <span>Total</span>
                <span className="text-green-600">₹{Math.round(discountedTotal).toLocaleString()}</span>
              </div>
            </div>
            
            {/* Minimum Order Progress */}
            {!isMinimumOrderMet && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Min. order: ₹{minimumOrderAmount.toLocaleString()}</span>
                  <span>₹{Math.round(minimumOrderAmount - discountedTotal).toLocaleString()} more needed</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((discountedTotal / minimumOrderAmount) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {!isMinimumOrderMet && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 text-center">
                  Add <span className="font-semibold">₹{Math.round(minimumOrderAmount - discountedTotal).toLocaleString()}</span> more to reach minimum order
                </p>
              </div>
            )}
            
            <Link
              to={isMinimumOrderMet ? "/checkout" : "#"}
              onClick={(e) => {
                if (!isMinimumOrderMet) {
                  e.preventDefault();
                }
              }}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 mb-4 block text-center relative overflow-hidden group ${
                isMinimumOrderMet
                  ? 'bg-accent-600 text-white hover:bg-accent-700 transform hover:scale-[1.02] shadow-lg hover:shadow-2xl hover:shadow-accent-500/25'
                  : 'bg-gray-400 text-gray-600 cursor-not-allowed'
              }`}
            >
              <span className="relative z-10">
                {isMinimumOrderMet ? 'Proceed to Enquiry' : `Min. Order ₹${minimumOrderAmount.toLocaleString()}`}
              </span>
              {isMinimumOrderMet && (
                <div className="absolute inset-0 bg-gradient-to-r from-accent-700 to-accent-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              )}
            </Link>
            
            <Link
              to="/"
              className="block text-center text-neutral-600 hover:text-accent-600 font-medium transition-all duration-300 hover:scale-105"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;